import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Reveal } from "@/components/Reveal";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Loader2, ArrowUpRight, Mail, MapPin, Phone } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = { name: "", email: "", company: "", interest: "Consulting", message: "", phone: "", consent: false, sms_consent: false };

const CHECKBOX_CLS = "mt-0.5 border-white/30 data-[state=checked]:bg-gradient-to-r data-[state=checked]:from-[#4f6bff] data-[state=checked]:to-[#9d6bff] data-[state=checked]:border-transparent data-[state=checked]:text-white";
const LINK_CLS = "text-white/85 underline underline-offset-4 decoration-white/30 hover:decoration-white transition-colors";

export default function Contact() {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email, and message.");
      return;
    }
    if (!form.consent) {
      toast.error("Please agree to the Privacy Policy and Terms to continue.");
      return;
    }
    if (form.sms_consent && !form.phone.trim()) {
      toast.error("Please add your mobile number to opt in to text messages.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Thanks! Your message is in — we'll be in touch shortly.");
      setForm(EMPTY);
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="relative bg-[#050505] py-24 lg:py-32 border-t border-white/10" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-2 gap-14">
        <Reveal>
          <div className="flex items-center gap-3 mb-5">
            <span className="h-px w-10 bg-gradient-to-r from-[#4f6bff] to-[#9d6bff]" />
            <span className="overline text-[#8b7cff]/90">06 &mdash; Let&apos;s talk</span>
          </div>
          <h2 className="font-display font-700 tracking-tighter leading-none text-white text-4xl sm:text-5xl mb-6">
            Book a discovery call.
          </h2>
          <p className="text-white/60 leading-relaxed max-w-md mb-10">
            Tell us about your goals and constraints. We&apos;ll come back with a candid view of
            what&apos;s possible, what it takes, and where to start.
          </p>

          <div className="space-y-5">
            <a href="mailto:ikumar@agentica-sol.com" className="flex items-center gap-4 text-white/70 hover:text-white transition-colors duration-300" data-testid="contact-email-link">
              <span className="h-11 w-11 rounded-full border border-white/10 flex items-center justify-center">
                <Mail className="h-4 w-4 text-[#8b7cff]" />
              </span>
              ikumar@agentica-sol.com
            </a>
            <a href="tel:+17542182448" className="flex items-center gap-4 text-white/70 hover:text-white transition-colors duration-300" data-testid="contact-phone-link">
              <span className="h-11 w-11 rounded-full border border-white/10 flex items-center justify-center">
                <Phone className="h-4 w-4 text-[#8b7cff]" />
              </span>
              +1 754 218 2448
            </a>
            <div className="flex items-center gap-4 text-white/70" data-testid="contact-address">
              <span className="h-11 w-11 rounded-full border border-white/10 flex items-center justify-center">
                <MapPin className="h-4 w-4 text-[#8b7cff]" />
              </span>
              Fort Lauderdale, Florida, United States
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <form
            onSubmit={submit}
            className="rounded-3xl border border-white/10 bg-white/[0.02] p-8 sm:p-10 space-y-6"
            data-testid="contact-form"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="name" className="text-white/70 text-xs">Name *</Label>
                <Input id="name" data-testid="contact-name-input"
                  value={form.name} onChange={(e) => update("name", e.target.value)}
                  placeholder="Ada Lovelace"
                  className="bg-black/40 border-white/10 text-white placeholder:text-white/30 focus-visible:ring-[#8b7cff]/40" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email" className="text-white/70 text-xs">Email *</Label>
                <Input id="email" type="email" data-testid="contact-email-input"
                  value={form.email} onChange={(e) => update("email", e.target.value)}
                  placeholder="you@company.com"
                  className="bg-black/40 border-white/10 text-white placeholder:text-white/30 focus-visible:ring-[#8b7cff]/40" />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="company" className="text-white/70 text-xs">Company</Label>
                <Input id="company" data-testid="contact-company-input"
                  value={form.company} onChange={(e) => update("company", e.target.value)}
                  placeholder="Acme Inc."
                  className="bg-black/40 border-white/10 text-white placeholder:text-white/30 focus-visible:ring-[#8b7cff]/40" />
              </div>
              <div className="space-y-2">
                <Label className="text-white/70 text-xs">I&apos;m interested in</Label>
                <Select value={form.interest} onValueChange={(v) => update("interest", v)}>
                  <SelectTrigger data-testid="contact-interest-select"
                    className="bg-black/40 border-white/10 text-white focus:ring-[#8b7cff]/40">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0a0a0a] border-white/10 text-white">
                    {["Consulting", "AI Engineering", "Agentic Products", "General"].map((o) => (
                      <SelectItem key={o} value={o} className="focus:bg-white/10 focus:text-white">{o}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="message" className="text-white/70 text-xs">Message *</Label>
              <Textarea id="message" data-testid="contact-message-input" rows={4}
                value={form.message} onChange={(e) => update("message", e.target.value)}
                placeholder="What are you trying to build?"
                className="bg-black/40 border-white/10 text-white placeholder:text-white/30 focus-visible:ring-[#8b7cff]/40 resize-none" />
            </div>

            <div className="flex items-start gap-3" data-testid="contact-consent-wrapper">
              <Checkbox
                id="consent"
                checked={form.consent}
                onCheckedChange={(v) => update("consent", v === true)}
                className={CHECKBOX_CLS}
                data-testid="contact-consent-checkbox"
              />
              <Label htmlFor="consent" className="text-white/55 text-xs leading-relaxed font-400 cursor-pointer">
                I agree to the{" "}
                <Link to="/privacy" className={LINK_CLS} data-testid="contact-privacy-link">Privacy Policy</Link>{" "}
                and{" "}
                <Link to="/terms" className={LINK_CLS} data-testid="contact-terms-link">Terms &amp; Conditions</Link>,
                and consent to be contacted by Agentica AI Solutions by email or phone about my inquiry. *
              </Label>
            </div>

            <div className="rounded-2xl border border-white/10 bg-black/30 p-5 space-y-4" data-testid="contact-sms-block">
              <div className="space-y-2">
                <Label htmlFor="phone" className="text-white/70 text-xs">Mobile number <span className="text-white/35">(optional)</span></Label>
                <Input id="phone" type="tel" data-testid="contact-phone-input"
                  value={form.phone} onChange={(e) => update("phone", e.target.value)}
                  placeholder="+1 555 123 4567"
                  className="bg-black/40 border-white/10 text-white placeholder:text-white/30 focus-visible:ring-[#8b7cff]/40" />
              </div>
              <div className="flex items-start gap-3" data-testid="contact-sms-consent-wrapper">
                <Checkbox
                  id="sms_consent"
                  checked={form.sms_consent}
                  onCheckedChange={(v) => update("sms_consent", v === true)}
                  className={CHECKBOX_CLS}
                  data-testid="contact-sms-consent-checkbox"
                />
                <Label htmlFor="sms_consent" className="text-white/55 text-xs leading-relaxed font-400 cursor-pointer">
                  <span className="text-white/80">Optional:</span> I agree to receive text messages from Agentica AI Solutions at the
                  mobile number above about my inquiry, such as scheduling and follow-ups. Message frequency varies. Message and data
                  rates may apply. Reply STOP to opt out, HELP for help. Consent is not a condition of purchase.
                </Label>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="group w-full inline-flex items-center justify-center gap-2 rounded-full bg-white text-void px-7 py-3.5 text-sm font-500 hover:bg-gradient-to-r hover:from-[#4f6bff] hover:to-[#9d6bff] hover:text-white transition-[background-color,color] duration-300 disabled:opacity-60"
              data-testid="contact-submit-button"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : (
                <>
                  Send message
                  <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </>
              )}
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
}
